import type { Editor, NodeEntry } from 'slate';
import { Node, Transforms } from 'slate';

import type { TableCellElement, TableElement, TableRowElement } from '../../internal';
import {
  createDefaultParagraph,
  isAlineContentBlock,
  isTableCellElement,
  isTableRowElement,
  Queries,
} from '../../internal';

export function normalizeTable(editor: Editor, tableEntry: NodeEntry<TableElement>): void {
  const [table, tablePath] = tableEntry;
  let hasAtLeastOneRow = false;

  for (const [child, childPath] of Node.children(table, [], { reverse: true })) {
    if (isTableRowElement(child)) {
      hasAtLeastOneRow = true;
    } else {
      Transforms.removeNodes(editor, { at: [...tablePath, ...childPath] });
    }
  }

  if (!hasAtLeastOneRow) return Transforms.removeNodes(editor, { at: tablePath });

  if (!Array.isArray(table.suggestedEdits)) {
    Transforms.setNodes(editor, { suggestedEdits: [] }, { at: tablePath });
  }
}

export function normalizeTableRow(editor: Editor, tableRowEntry: NodeEntry<TableRowElement>): void {
  const [tableRow, tableRowPath] = tableRowEntry;
  const hasAtLeastOneCell = tableRow.children.some(isTableCellElement);
  if (!hasAtLeastOneCell) return Transforms.removeNodes(editor, { at: tableRowPath });

  if (!Array.isArray(tableRow.suggestedEdits)) {
    Transforms.setNodes(editor, { suggestedEdits: [] }, { at: tableRowPath });
  }
}

export function normalizeTableCell(
  editor: Editor,
  tableCellEntry: NodeEntry<TableCellElement>
): void {
  const [tableCell, tableCellPath] = tableCellEntry;
  const state = { totalChildren: tableCell.children.length };
  tableCell.children.forEach((child, index) => {
    if (!isAlineContentBlock(child)) {
      Transforms.removeNodes(editor, { at: [...tableCellPath, index] });
      state.totalChildren--;
    }
  });

  if (state.totalChildren === 0) {
    const styleSheet = Queries.activeStyleSheet(editor);
    const textFormats = styleSheet['style-normal'].styles;

    Transforms.insertNodes(editor, createDefaultParagraph({ textProps: textFormats }), {
      at: [...tableCellPath, 0],
    });
  }

  if (!Array.isArray(tableCell.suggestedEdits)) {
    Transforms.setNodes(editor, { suggestedEdits: [] }, { at: tableCellPath });
  }
}
